import { useState, useEffect, useRef } from 'react';

/**
 * useScrollSpy - Track which section is currently visible in the viewport.
 *
 * Uses scroll position to find the element closest to a target zone in the viewport.
 * This approach is more reliable than IntersectionObserver for small header elements.
 *
 * @param elementIds - Array of element IDs to observe
 * @param options - Configuration options
 * @returns The ID of the currently active element, or null if none visible
 */
export function useScrollSpy(
  elementIds: string[],
  options: {
    targetPosition?: number; // 0-1, where in viewport to target (0.15 = 15% from top)
  } = {}
): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);
  const lastActiveRef = useRef<string | null>(null);

  const { targetPosition = 0.15 } = options;

  useEffect(() => {
    if (elementIds.length === 0) {
      setActiveId(null);
      return;
    }

    // Function to find the element closest to target position
    const updateActiveId = () => {
      const targetY = window.innerHeight * targetPosition;
      let bestMatch: { id: string; distance: number } | null = null;

      for (const id of elementIds) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          // Consider elements that are above or at the target line
          // (we want the most recent header we've scrolled past)
          if (rect.top <= targetY + 50) {
            const distance = Math.abs(rect.top - targetY);
            if (!bestMatch || rect.top > document.getElementById(bestMatch.id)!.getBoundingClientRect().top) {
              // Prefer the element that's closer to (but still above) the target
              bestMatch = { id, distance };
            }
          }
        }
      }

      // If no element is above the target, use the first one
      if (!bestMatch && elementIds.length > 0) {
        bestMatch = { id: elementIds[0], distance: 0 };
      }

      if (bestMatch && bestMatch.id !== lastActiveRef.current) {
        lastActiveRef.current = bestMatch.id;
        console.log(`[ScrollSpy] Active:`, bestMatch.id);
        setActiveId(bestMatch.id);
      }
    };

    // Throttle scroll handler
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateActiveId();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial update
    updateActiveId();

    // Debug log
    console.log(`[ScrollSpy] Tracking ${elementIds.length} elements`);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [elementIds.join(','), targetPosition]);

  return activeId;
}

export default useScrollSpy;
